import { oauthTokenRefresh } from '@/features/auth/oauthTokenRefresh'
import {
    AxiosError,
    AxiosInstance,
    AxiosResponse,
    InternalAxiosRequestConfig,
    isAxiosError,
} from 'axios'

interface RetryRequest {
    config: InternalAxiosRequestConfig
    resolve: (value: AxiosResponse) => void
    reject: (reason?: any) => void
}

export class UnauthedApiInterceptor {
    private axiosInstance: AxiosInstance
    private retryQueue: RetryRequest[] = []
    private isRefreshing = false

    constructor(axiosInstance: AxiosInstance) {
        this.axiosInstance = axiosInstance

        this.attachInterceptor()
    }

    private attachInterceptor = (): void => {
        this.axiosInstance.interceptors.response.use(
            (response) => response,
            (error) => this.onResponseError(error)
        )
    }

    private onResponseError = async (error: unknown): Promise<AxiosResponse> => {
        if (!this.isAxios401(error)) throw error

        const originalConfig = error.config as InternalAxiosRequestConfig

        if (this.isRefreshing) return this.enqueueRequest(originalConfig)

        this.isRefreshing = true

        const firstPromise = this.enqueueRequest(originalConfig)

        this.silenceUnhandled(firstPromise)

        try {
            const accessToken = await oauthTokenRefresh.refreshAccessToken()

            this.replayQueuedRequests(accessToken)

            return firstPromise
        } catch (e) {
            this.failQueuedRequests(e)

            throw e
        } finally {
            this.retryQueue = []
            this.isRefreshing = false
        }
    }

    private isAxios401 = (error: unknown): error is AxiosError => {
        return isAxiosError(error) && error.response?.status === 401 && !!error.config
    }

    private enqueueRequest = (config: InternalAxiosRequestConfig): Promise<AxiosResponse> => {
        return new Promise<AxiosResponse>((resolve, reject) => {
            this.retryQueue.push({ config, resolve, reject })
        })
    }

    private replayQueuedRequests = (newAccessToken: string): void => {
        this.retryQueue.forEach(({ config, resolve, reject }) => {
            config.headers.setAuthorization(`Bearer ${newAccessToken}`)

            this.axiosInstance
                .request(config)
                .then((resp) => resolve(resp))
                .catch((err) => reject(err))
        })
    }

    private failQueuedRequests = (error: unknown): void => {
        this.retryQueue.forEach(({ reject }) => reject(error))
    }

    // prevent unhandled rejection warning in Jest test
    private silenceUnhandled = (promise: Promise<AxiosResponse>): void => {
        promise.catch(() => {})
    }
}
